(() => {
  const pad = (value) => String(value).padStart(2, "0");

  class UniversalProductMedia {
    constructor(root) {
      this.root = root;
      this.modelPanels = [...root.querySelectorAll("[data-upm-model-panel]")];
      this.modelTabs = [...root.querySelectorAll("[data-upm-model-tab]")];
      this.rail = root.querySelector("[data-upm-rail]");
      this.tiles = [...root.querySelectorAll("[data-upm-tile]")];
      this.galleryCount = root.querySelector("[data-upm-gallery-count]");
      this.galleryGuide = root.querySelector(".upm__gallery-guide");
      this.dialog = root.querySelector("[data-upm-lightbox]");
      this.lightboxPanels = [...root.querySelectorAll("[data-upm-lightbox-panel]")];
      this.lightboxCount = root.querySelector("[data-upm-lightbox-count]");
      this.activeLightboxIndex = 0;
      this.lastTrigger = null;
      this.pointerStart = null;
      this.variants = this.readVariants();

      this.bindModels();
      this.bindGallery();
      this.bindLightbox();
      this.bindProductCard();
      if (this.modelPanels.length > 0) this.showModel(0);
    }

    bindModels() {
      this.modelTabs.forEach((tab) => {
        tab.addEventListener("click", () => this.showModel(Number(tab.dataset.upmModelTab)));
      });

    }

    loadModel(index) {
      const panel = this.modelPanels[index];
      if (!panel) return null;
      const host = panel.querySelector("[data-upm-model-host]");
      const template = panel.querySelector("[data-upm-model-template]");
      if (!host) return panel.querySelector("[data-upm-model-viewer]");

      if (!host.dataset.upmModelLoaded && template) {
        host.appendChild(template.content.cloneNode(true));
        host.dataset.upmModelLoaded = "true";
      }

      const viewer = host.querySelector("[data-upm-model-viewer]");
      if (!viewer || viewer.dataset.upmBound === "true") return viewer;
      viewer.dataset.upmBound = "true";
      const done = () => {
        panel.classList.add("is-loaded");
        this.refitModel(viewer);
      };
      viewer.addEventListener("load", done, { once: true });
      if (viewer.loaded) done();
      return viewer;
    }

    refitModel(viewer) {
      if (!viewer) return;
      const update = () => {
        window.dispatchEvent(new Event("resize"));
        if (typeof viewer.updateFraming === "function") viewer.updateFraming();
        if (typeof viewer.jumpCameraToGoal === "function") viewer.jumpCameraToGoal();
      };
      window.requestAnimationFrame(() => window.requestAnimationFrame(update));
    }

    showModel(index) {
      this.modelPanels.forEach((panel, panelIndex) => {
        const active = panelIndex === index;
        panel.hidden = !active;
        panel.classList.toggle("is-active", active);
      });
      this.modelTabs.forEach((tab, tabIndex) => {
        const active = tabIndex === index;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
      });
      this.refitModel(this.loadModel(index));
    }

    bindGallery() {
      if (!this.rail || this.tiles.length === 0) return;

      this.root.querySelector("[data-upm-gallery-previous]")?.addEventListener("click", () => this.stepRail(-1));
      this.root.querySelector("[data-upm-gallery-next]")?.addEventListener("click", () => this.stepRail(1));

      this.rail.addEventListener("scroll", () => {
        window.requestAnimationFrame(() => this.updateRailReadout());
      }, { passive: true });

      this.rail.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          this.stepRail(event.key === "ArrowLeft" ? -1 : 1);
        }
      });

      this.tiles.forEach((tile) => {
        tile.addEventListener("click", () => this.openLightbox(Number(tile.dataset.upmOpen), tile));
      });

      this.updateRailReadout();
    }

    stepRail(direction) {
      if (!this.rail || this.tiles.length === 0) return;
      const current = this.nearestTileIndex();
      const next = Math.max(0, Math.min(this.tiles.length - 1, current + direction));
      this.tiles[next]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
    }

    nearestTileIndex() {
      if (!this.rail) return 0;
      const left = this.rail.getBoundingClientRect().left;
      let nearest = 0;
      let distance = Number.POSITIVE_INFINITY;
      this.tiles.forEach((tile, index) => {
        const current = Math.abs(tile.getBoundingClientRect().left - left);
        if (current < distance) {
          nearest = index;
          distance = current;
        }
      });
      return nearest;
    }

    updateRailReadout() {
      const current = this.nearestTileIndex();
      if (this.galleryCount) this.galleryCount.textContent = `${pad(current + 1)} / ${pad(this.tiles.length)}`;
      if (this.galleryGuide) {
        const progress = this.tiles.length > 1 ? (current / (this.tiles.length - 1)) * 100 : 0;
        this.galleryGuide.style.setProperty("--upm-progress", `${progress}%`);
      }
    }

    bindLightbox() {
      if (!this.dialog) return;

      this.root.querySelector("[data-upm-close]")?.addEventListener("click", () => this.dialog.close());
      this.root.querySelector("[data-upm-lightbox-previous]")?.addEventListener("click", () => this.stepLightbox(-1));
      this.root.querySelector("[data-upm-lightbox-next]")?.addEventListener("click", () => this.stepLightbox(1));

      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener("keydown", (event) => {
        if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
          event.preventDefault();
          this.stepLightbox(event.key === "ArrowLeft" ? -1 : 1);
        }
      });

      this.dialog.addEventListener("close", () => {
        document.body.classList.remove("upm-lightbox-open");
        this.pauseLightboxMedia();
        this.lastTrigger?.focus({ preventScroll: true });
      });

      const stage = this.root.querySelector("[data-upm-lightbox-stage]");
      stage?.addEventListener("pointerdown", (event) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      });
      stage?.addEventListener("pointerup", (event) => {
        if (!this.pointerStart) return;
        const deltaX = event.clientX - this.pointerStart.x;
        const deltaY = event.clientY - this.pointerStart.y;
        this.pointerStart = null;
        if (Math.abs(deltaX) > 54 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
          this.stepLightbox(deltaX < 0 ? 1 : -1);
        }
      });
      stage?.addEventListener("pointercancel", () => { this.pointerStart = null; });
    }

    openLightbox(index, trigger) {
      if (!this.dialog || this.lightboxPanels.length === 0) return;
      this.lastTrigger = trigger;
      this.showLightbox(index, 0);
      document.body.classList.add("upm-lightbox-open");
      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
    }

    stepLightbox(direction) {
      if (this.lightboxPanels.length < 2) return;
      const next = (this.activeLightboxIndex + direction + this.lightboxPanels.length) % this.lightboxPanels.length;
      this.showLightbox(next, direction);
    }

    showLightbox(index, direction) {
      this.pauseLightboxMedia();
      this.activeLightboxIndex = index;
      this.lightboxPanels.forEach((panel, panelIndex) => {
        panel.hidden = panelIndex !== index;
        panel.classList.remove("is-entering-next", "is-entering-previous");
      });
      const active = this.lightboxPanels[index];
      if (active && direction !== 0) {
        void active.offsetWidth;
        active.classList.add(direction > 0 ? "is-entering-next" : "is-entering-previous");
      }
      if (this.lightboxCount) this.lightboxCount.textContent = `${pad(index + 1)} / ${pad(this.lightboxPanels.length)}`;
    }

    pauseLightboxMedia() {
      this.lightboxPanels.forEach((panel) => {
        panel.querySelectorAll("video").forEach((video) => video.pause());
      });
    }

    readVariants() {
      const node = this.root.querySelector("[data-upm-variants]");
      if (!node) return [];
      try {
        return JSON.parse(node.textContent || "[]");
      } catch (error) {
        console.warn("EXO universal product media could not read variants.", error);
        return [];
      }
    }

    bindProductCard() {
      const selects = [...this.root.querySelectorAll("[data-upm-option]")];
      selects.forEach((select) => select.addEventListener("change", () => this.updateVariant(selects)));

      this.root.querySelector("[data-upm-configure]")?.addEventListener("click", () => {
        const externalForms = [...document.querySelectorAll('#MainContent form[action*="/cart/add"]')]
          .filter((form) => !this.root.contains(form));
        const target = externalForms[0]?.closest("section, [id], form") || document.querySelector("#MainContent .shopify-section:nth-of-type(2)");
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
        const firstControl = externalForms[0]?.querySelector("select, input:not([type='hidden']), button");
        window.setTimeout(() => firstControl?.focus({ preventScroll: true }), 500);
      });
    }

    updateVariant(selects) {
      if (this.variants.length === 0) return;
      const values = selects.map((select) => select.value);
      const variant = this.variants.find((candidate) => values.every((value, index) => candidate[`option${index + 1}`] === value));
      if (!variant) return;

      const variantInput = this.root.querySelector("[data-upm-variant]");
      if (variantInput) variantInput.value = variant.id;
      const price = this.root.querySelector("[data-upm-price]");
      if (price) price.textContent = this.formatMoney(variant.price);
      const sku = this.root.querySelector("[data-upm-sku]");
      if (sku) sku.textContent = variant.sku || "ASSIGNED AT RELEASE";
      const state = this.root.querySelector("[data-upm-state]");
      if (state) state.textContent = variant.available ? "AVAILABLE" : "CURRENTLY UNAVAILABLE";
      const submit = this.root.querySelector("[data-upm-submit]");
      if (submit) submit.disabled = !variant.available;
      const submitLabel = this.root.querySelector("[data-upm-submit-label]");
      if (submitLabel) submitLabel.textContent = variant.available ? "ADD TO CART" : "CURRENTLY UNAVAILABLE";
      const primaryImage = this.root.querySelector("[data-upm-primary-image], .upm__fallback img");
      const variantImage = variant.featured_image?.src || variant.featured_image?.url;
      if (primaryImage && variantImage) {
        primaryImage.removeAttribute("srcset");
        primaryImage.src = variantImage;
        primaryImage.alt = variant.featured_media?.alt || `${this.root.dataset.productHandle || "Product"} selected variant`;
      }

      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }

    formatMoney(cents) {
      const currency = window.Shopify?.currency?.active || document.documentElement.dataset.currency || "USD";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", { style: "currency", currency }).format(Number(cents) / 100);
      } catch (_error) {
        return `$${(Number(cents) / 100).toFixed(2)}`;
      }
    }
  }

  const initialize = () => {
    document.querySelectorAll("[data-upm]").forEach((root) => {
      if (root.dataset.upmReady === "true") return;
      root.dataset.upmReady = "true";
      new UniversalProductMedia(root);
    });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initialize, { once: true });
  else initialize();
  document.addEventListener("shopify:section:load", initialize);
})();
